import React, { useState, useEffect } from 'react';
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  Box,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import { format } from 'date-fns';
import api from '../../utils/api';

const ReportedMessages = () => {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statusFilter, setStatusFilter] = useState('');
  const [selectedReport, setSelectedReport] = useState(null);
  const [adminNotes, setAdminNotes] = useState('');
  const [updating, setUpdating] = useState(false);

  const fetchReports = async () => {
    try {
      setError(null);
      const params = statusFilter ? { status: statusFilter } : {};
      const response = await api.get('/api/admin/reported-messages', { params });
      setReports(response.data);
    } catch (err) {
      setError('Failed to load reported messages.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReports();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter]);

  const handleUpdateStatus = async (reportId, newStatus) => {
    setUpdating(true);
    try {
      const payload = { status: newStatus };
      if (selectedReport?._id === reportId && adminNotes !== undefined) {
        payload.adminNotes = adminNotes;
      }
      await api.patch(`/api/admin/reported-messages/${reportId}`, payload);
      await fetchReports();
      setSelectedReport(null);
      setAdminNotes('');
    } catch (err) {
      setError('Failed to update report.');
    } finally {
      setUpdating(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'warning';
      case 'reviewed': return 'info';
      case 'resolved': return 'success';
      case 'dismissed': return 'default';
      default: return 'default';
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h5" gutterBottom>
        Reported Messages
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Review and take action on messages reported by users.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <FormControl size="small" sx={{ minWidth: 160, mb: 2 }}>
        <InputLabel>Filter by status</InputLabel>
        <Select
          value={statusFilter}
          label="Filter by status"
          onChange={(e) => setStatusFilter(e.target.value)}
        >
          <MenuItem value="">All</MenuItem>
          <MenuItem value="pending">Pending</MenuItem>
          <MenuItem value="reviewed">Reviewed</MenuItem>
          <MenuItem value="resolved">Resolved</MenuItem>
          <MenuItem value="dismissed">Dismissed</MenuItem>
        </Select>
      </FormControl>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Reported</TableCell>
              <TableCell>Message</TableCell>
              <TableCell>Sender</TableCell>
              <TableCell>Chat</TableCell>
              <TableCell>Reporter</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {reports.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                  No reported messages found.
                </TableCell>
              </TableRow>
            ) : (
              reports.map((report) => (
                <TableRow key={report._id}>
                  <TableCell>
                    {format(new Date(report.reportedAt), 'dd MMM yyyy, HH:mm')}
                  </TableCell>
                  <TableCell sx={{ maxWidth: 200 }}>
                    <Typography variant="body2" noWrap title={report.messageContent}>
                      {report.messageContent?.substring(0, 60)}...
                    </Typography>
                  </TableCell>
                  <TableCell>{report.messageSenderType}</TableCell>
                  <TableCell>
                    {report.chat?.wedding?.name || report.chat?.business?.name || report.chat?.title || '-'}
                  </TableCell>
                  <TableCell>
                    {report.reportedBy?.firstName} {report.reportedBy?.lastName}
                    <Typography variant="caption" display="block">
                      {report.reportedByType}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip label={report.status} color={getStatusColor(report.status)} size="small" />
                  </TableCell>
                  <TableCell align="right">
                    <Button
                      size="small"
                      onClick={() => {
                        setSelectedReport(report);
                        setAdminNotes(report.adminNotes || '');
                      }}
                    >
                      View
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={!!selectedReport} onClose={() => setSelectedReport(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Reported Message</DialogTitle>
        <DialogContent>
          {selectedReport && (
            <>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Message content:
              </Typography>
              <Paper variant="outlined" sx={{ p: 2, mb: 2, bgcolor: 'grey.50' }}>
                <Typography variant="body1">{selectedReport.messageContent}</Typography>
              </Paper>
              <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                Sent by {selectedReport.messageSenderType} on{' '}
                {format(new Date(selectedReport.messageTimestamp), 'dd MMM yyyy, HH:mm')}
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                Reported by {selectedReport.reportedBy?.firstName} {selectedReport.reportedBy?.lastName} ({selectedReport.reportedByType}) on{' '}
                {format(new Date(selectedReport.reportedAt), 'dd MMM yyyy, HH:mm')}
              </Typography>
              <TextField
                label="Admin notes"
                multiline
                rows={2}
                value={adminNotes}
                onChange={(e) => setAdminNotes(e.target.value)}
                fullWidth
                sx={{ mt: 2 }}
              />
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSelectedReport(null)}>Close</Button>
          {selectedReport?.status === 'pending' && (
            <>
              <Button
                onClick={() => handleUpdateStatus(selectedReport._id, 'dismissed')}
                disabled={updating}
              >
                Dismiss
              </Button>
              <Button
                onClick={() => handleUpdateStatus(selectedReport._id, 'reviewed')}
                disabled={updating}
              >
                Mark reviewed
              </Button>
              <Button
                variant="contained"
                onClick={() => handleUpdateStatus(selectedReport._id, 'resolved')}
                disabled={updating}
              >
                Resolve
              </Button>
            </>
          )}
          {selectedReport?.status === 'reviewed' && (
            <Button
              variant="contained"
              onClick={() => handleUpdateStatus(selectedReport._id, 'resolved')}
              disabled={updating}
            >
              Resolve
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ReportedMessages;
